import { UI_Heading } from 'ui/components/1-atoms/Heading/Heading';
import { UI_Picture } from 'ui/components/1-atoms/Media/Picture/Picture';
import { UI_Player } from 'ui/components/1-atoms/Media/Player/Player';
import { UI_Container } from 'ui/components/4-habitats/Container/Container';
import { UI_Grid } from 'ui/components/4-habitats/Grid/Grid';
import { UI_GridCell } from 'ui/components/4-habitats/Grid/GridCell';
import { UI_TopLevelGrid } from 'ui/components/4-habitats/TopLevelGrid/TopLevelGrid';
import { UI_TopLevelGridCell } from 'ui/components/4-habitats/TopLevelGrid/TopLevelGridCell';
import ColorOptions from 'helpers/types';
import classNames from 'classnames';
import { UI_LinkButton, UI_LinkButtonProps } from 'ui/components/1-atoms/Action/LinkButton/LinkButton';
import { useInView } from 'react-intersection-observer';
import { useEffect, useState } from 'react';
import { UI_RichText } from 'ui/components/1-atoms/RichText/RichText';
import styles from './MediaSpot.module.scss';

export interface UI_MediaSpotProps {
	heading: string;
	contentHeading: string;
	contentParagraph: string;
	bgColor?: ColorOptions;
	trumpetHeading?: string;
	image?: {
		url: string;
		altText?: string;
		imageCropping?: 'cover' | 'contain';
		focalPoint?: { left: number; top: number };
		fileType?: string;
	};
	video?: {
		url: string;
		poster?: string;
		autoplay?: boolean;
		showControls?: boolean;
		isMuted?: boolean;
		playOnMouseover?: boolean;
	};
	linkButton?: UI_LinkButtonProps;
	readMoreText: string;
	readMoreButtonText: string;
	showControls?: boolean;
	isMuted?: boolean;
	linkButtonStyle?: 'primary' | 'secondary';
	mobileImage?: {
		url: string;
		altText?: string;
		imageCropping?: 'cover' | 'contain';
		focalPoint?: { left: number; top: number };
	};
}

export const UI_MediaSpot: React.FC<UI_MediaSpotProps> = ({
	bgColor = 'white',
	trumpetHeading,
	heading,
	image,
	video,
	contentHeading,
	contentParagraph,
	linkButton,
	showControls = false,
	isMuted = true,
	linkButtonStyle,
	mobileImage,
}) => {
	const { ref, inView } = useInView({
		threshold: 0.5,
		triggerOnce: false,
	});

	const [isPlaying, setIsPlaying] = useState(false);
	const [isTouchDevice, setIsTouchDevice] = useState(false);
	const onVideoMouseEnter = () => {
		if (video?.playOnMouseover) {
			setIsPlaying(true);
		}
	};

	useEffect(() => {
		const isTouch = window?.matchMedia('(hover: none)').matches;
		setIsTouchDevice(isTouch);
	}, []);

	return (
		<div ref={ref} className={classNames(styles.MediaSpot, `u-color___${bgColor}`)}>
			<UI_Container width="Standard">
				<UI_TopLevelGrid container wrap>
					<UI_TopLevelGridCell desktopWidth="16col" mobileWidth="100">
						{trumpetHeading && <p className={styles.MediaSpot_trumpetHeading}>{trumpetHeading}</p>}

						{heading && (
							<UI_Heading className={styles.MediaSpot_heading} headingLevel="h2" style="xl">
								{heading}
							</UI_Heading>
						)}
					</UI_TopLevelGridCell>
					<UI_TopLevelGridCell desktopWidth="22col" mobileWidth="100" desktopOffset="2col" offsetDirection="right">
						<UI_Grid container wrap largeGutter>
							{(video || image || mobileImage) && (
								<UI_GridCell desktopWidth="66" mobileWidth="100" className={styles.MediaSpot_media}>
									<div onMouseEnter={onVideoMouseEnter}>
										{video ? ( //video might need to be in a 16,9 ratio
											<UI_Player
												url={video.url}
												isLight={video.autoplay || isPlaying || (isTouchDevice && inView) ? false : true}
												playing={isPlaying || inView ? true : false}
												muted={isMuted}
												loop={true}
												controls={showControls}
												poster={image?.url}
											/>
										) : (
											<>
												{mobileImage && image ? (
													<>
														<div className={styles.MediaSpot_mobileMedia}>
															<UI_Picture
																className={styles.MediaSpot_imgWrapper}
																url={mobileImage.url}
																altText={mobileImage.altText}
																imageCropping={mobileImage.imageCropping ?? 'cover'}
																focalPoint={mobileImage.focalPoint}
																sizes="100vw"
															/>
														</div>
														<div className={styles.MediaSpot_desktopMedia}>
															<UI_Picture
																className={styles.MediaSpot_imgWrapper}
																url={image.url}
																altText={image.altText}
																imageCropping={image.imageCropping ?? 'cover'}
																focalPoint={image.focalPoint}
																sizes="(max-width: 768px) 100vw, (max-width: 1728px) 66vw, 1150px"
																fileType={image.fileType}
															/>
														</div>
													</>
												) : (
													image && (
														<UI_Picture
															className={styles.MediaSpot_imgWrapper}
															url={image.url}
															altText={image.altText}
															imageCropping={image.imageCropping ?? 'cover'}
															focalPoint={image.focalPoint}
															sizes="(max-width: 768px) 100vw, (max-width: 1728px) 66vw, 1150px"
															fileType={image.fileType}
														/>
													)
												)}
											</>
										)}
									</div>
								</UI_GridCell>
							)}

							<UI_GridCell desktopWidth="33" mobileWidth="100" className={styles.MediaSpot_contentText}>
								{contentHeading && (
									<UI_Heading headingLevel="h3" className={styles.MediaSpot_contentHeading}>
										{contentHeading}
									</UI_Heading>
								)}

								{contentParagraph && <UI_RichText content={contentParagraph} className={styles.MediaSpot_contentParagraph} />}

								{linkButton && (
									<UI_LinkButton style={linkButtonStyle} href={linkButton.href} target={linkButton.target} inheritsColor>
										{linkButton.title}
									</UI_LinkButton>
								)}
							</UI_GridCell>
						</UI_Grid>
					</UI_TopLevelGridCell>
				</UI_TopLevelGrid>
			</UI_Container>
		</div>
	);
};
